import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Pill } from "lucide-react";

/**
 * Live drug autosuggest. As user types ≥2 chars hits /drugs/search.
 * onPick gets the full drug object so caller can append into its list.
 */
export default function DrugAutoSuggest({ onPick }) {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const debounce = useRef(null);

  useEffect(() => {
    if (debounce.current) clearTimeout(debounce.current);
    if (!q || q.length < 2) { setItems([]); return; }
    debounce.current = setTimeout(async () => {
      try {
        const r = await api.get(`/drugs/search?q=${encodeURIComponent(q)}`);
        setItems(r.data.items || []);
        setOpen(true);
      } catch { /* noop */ }
    }, 200);
  }, [q]);

  const pick = (d) => {
    onPick?.(d);
    setQ(""); setItems([]); setOpen(false);
  };

  return (
    <div className="relative">
      <div className="flex items-center gap-2">
        <Pill className="h-4 w-4 text-[#f37021]" />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search drug (e.g. amoxicillin, pantoprazole)…"
          className="flex-1"
          data-testid="drug-search-input"
          onFocus={() => items.length && setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 200)}
        />
      </div>
      {open && items.length > 0 && (
        <div className="absolute z-30 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-xl max-h-72 overflow-auto" data-testid="drug-suggestions">
          {items.map((d, i) => (
            <button
              key={i}
              type="button"
              onClick={() => pick(d)}
              className="w-full text-left px-3 py-2 hover:bg-[#e6f0ff] border-b border-slate-100 last:border-b-0"
              data-testid={`drug-suggestion-${i}`}
            >
              <div className="font-semibold text-slate-900">{d.name}</div>
              <div className="text-xs text-slate-500">{d.category} · {d.default_dose} · {d.default_freq}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
